import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, Reorder } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Menu,
  GripVertical,
  Save,
  RotateCcw,
  Eye,
  EyeOff,
  Pencil,
  Check,
  X,
  Loader2,
  ArrowLeft,
  Monitor,
  Package,
  Cpu,
  Code,
  Wrench,
  Image,
  FileSpreadsheet,
  Phone
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map icon names to components
const ICON_MAP = {
  Monitor: Monitor,
  Package: Package,
  Cpu: Cpu,
  Code: Code,
  Wrench: Wrench,
  Image: Image,
  FileSpreadsheet: FileSpreadsheet,
  Phone: Phone
};

// Helper function to compare menu items
const areMenuItemsDifferent = (items, original) => {
  if (items.length !== original.length) return true;
  for (let i = 0; i < items.length; i++) {
    const a = items[i];
    const b = original[i];
    if (a.key !== b.key || a.label_fr !== b.label_fr || a.label_en !== b.label_en || 
        a.visible !== b.visible || a.order !== b.order) {
      return true;
    }
  }
  return false;
};

export default function MenuManagement() {
  const { token, isAdmin, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editForm, setEditForm] = useState({ label_fr: '', label_en: '' });

  // Calculate hasChanges using useMemo
  const hasChanges = useMemo(() => {
    if (originalItems.length === 0) return false;
    return areMenuItemsDifferent(menuItems, originalItems);
  }, [menuItems, originalItems]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isAdmin()) {
      navigate('/');
      return;
    }
    fetchMenuItems();
  }, [isAuthenticated, authLoading, isAdmin, navigate]);

  const fetchMenuItems = async () => {
    try {
      const res = await axios.get(`${API}/admin/menu-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMenuItems(res.data);
      setOriginalItems(JSON.parse(JSON.stringify(res.data)));
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast.error('Erreur lors du chargement du menu');
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = (newOrder) => {
    const updatedItems = [];
    for (let i = 0; i < newOrder.length; i++) {
      const item = newOrder[i];
      updatedItems.push({
        key: item.key,
        label_fr: item.label_fr,
        label_en: item.label_en,
        href: item.href,
        icon: item.icon,
        visible: item.visible,
        order: i
      });
    }
    setMenuItems(updatedItems);
  };

  const handleToggleVisibility = (key) => {
    const updated = menuItems.map(item => 
      item.key === key ? { ...item, visible: !item.visible } : item
    );
    setMenuItems(updated);
  };

  const startEditing = (item) => {
    setEditingKey(item.key);
    setEditForm({ label_fr: item.label_fr, label_en: item.label_en });
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setEditForm({ label_fr: '', label_en: '' });
  };

  const saveEditing = () => {
    const updated = menuItems.map(item => 
      item.key === editingKey 
        ? { ...item, label_fr: editForm.label_fr, label_en: editForm.label_en }
        : item
    );
    setMenuItems(updated);
    setEditingKey(null);
    setEditForm({ label_fr: '', label_en: '' });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/menu-items`,
        { items: menuItems },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOriginalItems(JSON.parse(JSON.stringify(menuItems)));
      toast.success('Menu mis à jour avec succès!');
    } catch (error) {
      console.error('Error saving menu:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setMenuItems(JSON.parse(JSON.stringify(originalItems)));
    setEditingKey(null);
    toast.info('Modifications annulées');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 text-black animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin()) return null;

  return (
    <div className="min-h-screen py-8 bg-gray-50" data-testid="menu-management-page">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/admin')}
                className="text-gray-600 hover:text-black"
                data-testid="back-to-admin-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                <Menu className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-black">Gestion du Menu</h1>
                <p className="text-gray-500">Réorganisez, affichez/masquez et renommez les onglets</p>
              </div>
            </div>
            <div className="flex gap-2">
              {hasChanges && (
                <Button 
                  variant="outline" 
                  onClick={handleReset}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  data-testid="reset-menu-btn"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Annuler
                </Button>
              )}
              <Button 
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="bg-black text-white hover:bg-gray-800 disabled:opacity-50"
                data-testid="save-menu-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Sauvegarder
              </Button>
            </div>
          </div>

          {/* Instructions */}
          <Card className="bg-white border-gray-200 mb-6">
            <CardContent className="p-4">
              <div className="flex items-start gap-3 text-sm text-gray-600">
                <div className="flex flex-col gap-2">
                  <p><strong>Glissez-déposez</strong> pour réorganiser les onglets du menu.</p>
                  <p><strong>Toggle</strong> pour afficher/masquer un onglet instantanément.</p>
                  <p><strong>Crayon</strong> pour renommer les labels FR et EN.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Menu Items List */}
          <Card className="bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="text-black flex items-center gap-2">
                <Menu className="h-5 w-5" />
                Navigation publique
              </CardTitle>
              <CardDescription>
                {menuItems.filter(i => i.visible).length} / {menuItems.length} onglets visibles
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : (
                <Reorder.Group 
                  axis="y" 
                  values={menuItems} 
                  onReorder={handleReorder}
                  className="space-y-2"
                >
                  {menuItems.map((item) => {
                    const IconComponent = ICON_MAP[item.icon] || Menu;
                    const isEditing = editingKey === item.key;
                    
                    return (
                      <Reorder.Item
                        key={item.key}
                        value={item}
                        className={`flex items-center gap-4 p-4 rounded-lg border transition-all cursor-grab active:cursor-grabbing ${
                          item.visible 
                            ? 'bg-white border-gray-200 hover:border-gray-300' 
                            : 'bg-gray-50 border-gray-100 opacity-60'
                        }`}
                        data-testid={`menu-item-${item.key}`}
                      >
                        {/* Drag Handle */}
                        <div className="text-gray-400 hover:text-gray-600">
                          <GripVertical className="h-5 w-5" />
                        </div>

                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          item.visible ? 'bg-black' : 'bg-gray-300'
                        }`}>
                          <IconComponent className={`h-5 w-5 ${item.visible ? 'text-white' : 'text-gray-500'}`} />
                        </div>

                        {/* Labels */}
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <Label className="text-xs text-gray-500">FR</Label>
                                <Input
                                  value={editForm.label_fr}
                                  onChange={(e) => setEditForm({ ...editForm, label_fr: e.target.value })}
                                  className="h-8 text-sm"
                                  data-testid={`edit-label-fr-${item.key}`}
                                />
                              </div>
                              <div className="flex-1">
                                <Label className="text-xs text-gray-500">EN</Label>
                                <Input
                                  value={editForm.label_en}
                                  onChange={(e) => setEditForm({ ...editForm, label_en: e.target.value })}
                                  className="h-8 text-sm"
                                  data-testid={`edit-label-en-${item.key}`}
                                />
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-black">{item.label_fr}</span>
                                <Badge variant="outline" className="text-xs">FR</Badge>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <span>{item.label_en}</span>
                                <Badge variant="outline" className="text-xs">EN</Badge>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Path */}
                        <code className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded hidden sm:block">
                          {item.href}
                        </code>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {isEditing ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={saveEditing}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                data-testid={`save-edit-${item.key}`}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={cancelEditing}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`cancel-edit-${item.key}`}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => startEditing(item)}
                              className="text-gray-500 hover:text-black hover:bg-gray-100"
                              data-testid={`edit-btn-${item.key}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}

                          {/* Visibility Toggle */}
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={item.visible}
                              onCheckedChange={() => handleToggleVisibility(item.key)}
                              data-testid={`toggle-${item.key}`}
                            />
                            {item.visible ? (
                              <Eye className="h-4 w-4 text-green-600" />
                            ) : (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </Reorder.Item>
                    );
                  })}
                </Reorder.Group>
              )}
            </CardContent>
          </Card>

          {/* Preview Notice */}
          <Card className="bg-gray-900 border-gray-800 mt-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 text-white">
                <Eye className="h-5 w-5 text-gray-400" />
                <div className="text-sm">
                  <p className="font-medium">Aperçu en temps réel</p>
                  <p className="text-gray-400">Les changements seront visibles immédiatement après avoir cliqué sur &quot;Sauvegarder&quot;.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
